import { ref, computed, watchEffect, isRef, unref } from 'vue'
import { get } from '@/api'

/**
 * Composable for fetching and managing remote repository files.
 *
 * @param {Object} options
 * @param {Ref<string>|ComputedRef<string>|string} options.remoteUrl - Repository URL (reactive or plain)
 * @param {Ref<string>|ComputedRef<string>|string} options.commitHash - Commit hash (reactive or plain)
 * @param {Ref<string>|ComputedRef<string>|string} options.workflowVersionId - Workflow version ID (reactive or plain)
 *
 * @example
 * // With reactive refs (recommended - auto-fetches on change)
 * const { remoteFiles } = useRemoteRepositoryFiles({
 *   remoteUrl: repositoryUrl,
 *   commitHash: selectedCommitHash,
 * })
 */
export function useRemoteRepositoryFiles({
  remoteUrl: remoteUrlParam = null,
  commitHash: commitHashParam = null,
  workflowVersionId: workflowVersionIdParam = null,
} = {}) {
  // Accept both refs and plain values for flexibility
  // Note: Vue 2's toRef() requires (object, key) signature, so we use isRef() check
  const remoteUrl = isRef(remoteUrlParam) ? remoteUrlParam : ref(remoteUrlParam)
  const commitHash = isRef(commitHashParam)
    ? commitHashParam
    : ref(commitHashParam)
  const workflowVersionId = isRef(workflowVersionIdParam)
    ? workflowVersionIdParam
    : ref(workflowVersionIdParam)

  const remoteFiles = ref([])
  const isLoadingRepositoryFiles = ref(false)
  const workflowManager = ref(null)
  const listRemoteError = ref(null)

  // Track the last requested URL+commit to avoid stale requests
  let lastRequestKey = null

  const urlSearchParams = computed(() => {
    const searchParams = new URLSearchParams()
    if (unref(workflowVersionId)) {
      searchParams.append('workflowversion_id', unref(workflowVersionId))
    } else {
      searchParams.append('remote_repository', unref(remoteUrl))
      searchParams.append('commit_hash', unref(commitHash))
    }
    return searchParams
  })

  async function listRepositoryFiles() {
    const currentUrl = unref(remoteUrl)
    const currentCommitHash = unref(commitHash)
    const currentWorkflowVersionId = unref(workflowVersionId)

    // Skip if we don't have valid parameters
    if (!currentWorkflowVersionId && (!currentUrl || !currentCommitHash)) {
      return remoteFiles
    }

    const requestKey = `${currentUrl}:${currentCommitHash}:${currentWorkflowVersionId}`

    remoteFiles.value = []
    let listRepoFilesUrl = `workflows/workflows/list-repository-files/?`
    listRepoFilesUrl +=
      urlSearchParams.value && urlSearchParams.value.toString()
    isLoadingRepositoryFiles.value = true
    lastRequestKey = requestKey

    try {
      const response = await get(listRepoFilesUrl)
      // Only update if this is still the current request
      if (lastRequestKey === requestKey && response.status === 200) {
        workflowManager.value = response.data.results.workflow_manager || ''
        remoteFiles.value = response.data.results.files || []
      }
    } catch (e) {
      // Only update error if this is still the current request
      if (lastRequestKey === requestKey) {
        listRemoteError.value = e?.response?.data?.message || e?.message || null
      }
    } finally {
      if (lastRequestKey === requestKey) {
        isLoadingRepositoryFiles.value = false
      }
    }
    return remoteFiles
  }

  function resetFiles() {
    remoteFiles.value = []
    listRemoteError.value = null
    lastRequestKey = null
  }

  watchEffect(() => {
    const currentUrl = unref(remoteUrl)
    const currentCommitHash = unref(commitHash)
    const currentWorkflowVersionId = unref(workflowVersionId)

    // Only fetch if we have valid, complete parameters
    if (currentWorkflowVersionId) {
      listRepositoryFiles()
    } else if (currentUrl && currentCommitHash) {
      listRepositoryFiles()
    }
  })

  return {
    remoteFiles,
    listRepositoryFiles,
    isLoadingRepositoryFiles,
    workflowManager,
    listRemoteError,
    resetFiles,
  }
}
